import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiUrl } from '../config/api.config';

// NOTE: Do NOT call getApiUrl() at module load time!
// It must be called at request time when window.location is available.

// Helper to get axios config with runtime API URL
const getAxiosConfig = () => ({
  withCredentials: true,
  timeout: 10000
});

// Cache for tickers (avoid refetching)
let tickersCache = null;
let tickersCacheTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Async thunks - use getApiUrl() at request time, not module load time
export const fetchAllTickers = createAsyncThunk(
  'tickers/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      // Use cache if valid
      const now = Date.now();
      if (tickersCache && (now - tickersCacheTime) < CACHE_DURATION) {
        return tickersCache;
      }

      const response = await axios.get(`${getApiUrl()}/tickers`, getAxiosConfig());
      tickersCache = response.data;
      tickersCacheTime = now;
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Request failed');
    }
  }
);

export const fetchTickerInfo = createAsyncThunk(
  'tickers/fetchInfo',
  async (ticker, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${getApiUrl()}/tickers/${ticker}`, getAxiosConfig());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Request failed');
    }
  }
);

// Tickers slice
const tickersSlice = createSlice({
  name: 'tickers',
  initialState: {
    tickers: [],
    tickerInfo: {},
    loading: false,
    error: null,
  },
  reducers: {
    clearTickerInfo: (state) => {
      state.tickerInfo = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all tickers
      .addCase(fetchAllTickers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTickers.fulfilled, (state, action) => {
        state.loading = false;
        state.tickers = action.payload.tickers;
      })
      .addCase(fetchAllTickers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch ticker info
      .addCase(fetchTickerInfo.fulfilled, (state, action) => {
        state.tickerInfo[action.payload.ticker] = action.payload;
      });
  },
});

export const { clearTickerInfo } = tickersSlice.actions;
export default tickersSlice.reducer;